import React, { useMemo, useState } from 'react';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputTextarea } from 'primereact/inputtextarea';
import { PrimeIcons } from 'primereact/api';
import { useClient } from '../../../hooks/useClient';
import { ExternalUser } from '../../../types/externalUser';
import { mutate } from 'swr';
import { useOrganizationContext } from '../../../context/OrganizationContext';
import { useForm } from '../../../hooks/useForm';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { format } from 'date-fns';

const MAX_OBJECTIVE_CHARS = 900;

interface ObjectiveFieldProps {
  objective: string | undefined;
  externalUserId: string | undefined;
  hasEditFeature: boolean;
}

interface ObjectiveForm {
  objective: string;
}

const getInitialForm = (objective: string | undefined): ObjectiveForm => ({
  objective: objective ?? '',
});

export const ObjectiveField = ({ objective, externalUserId, hasEditFeature }: ObjectiveFieldProps) => {
  const { patch } = useClient();
  const { organization } = useOrganizationContext();
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [wasOpenedInEditMode, setWasOpenedInEditMode] = useState(false);
  const [showConfirmClose, setShowConfirmClose] = useState(false);
  const [closeAction, setCloseAction] = useState<() => void>(() => () => {});

  const initialForm = useMemo(() => getInitialForm(objective), [objective]);
  const { form, setFormFields, isSaving, setIsSaving, hasChanged, resetForm } = useForm<ObjectiveForm>(initialForm, !showModal);

  const truncatedText = useMemo(() => {
    if (!objective) return '';
    if (objective.length <= MAX_OBJECTIVE_CHARS) return objective;
    return objective.slice(0, MAX_OBJECTIVE_CHARS) + '...';
  }, [objective]);

  const needsTruncation = objective && objective.length > MAX_OBJECTIVE_CHARS;

  const handleSave = async () => {
    if (!organization || !externalUserId || !form) return;
    setIsSaving(true);

    const response = await patch<ExternalUser>(`/external-users/${externalUserId}`, {
      body: { objective: form.objective },
      successMessage: {
        summary: 'Objetivo actualizado',
        detail: 'El objetivo ha sido actualizado correctamente',
      },
      errorMessages: {
        summary: 'Error al actualizar el objetivo',
        defaultDetail: 'No se ha podido actualizar el objetivo',
      },
    });

    if (response) {
      mutate(`/organizations/${organization.id}/external-users/${externalUserId}`);
      setIsEditing(false);
      setShowModal(false);
    }
    setIsSaving(false);
  };

  const addTodayDate = (objective: string) => {
    const today = new Date();
    const formattedDate = format(today, "dd 'de' MMMM 'de' yyyy");
    return `==${formattedDate}==\n\n\n\n${objective}`;
  };

  const handleOpenModal = (startEditing: boolean = false) => {
    resetForm();
    setIsEditing(startEditing);
    setWasOpenedInEditMode(startEditing);
    setShowModal(true);
    if (startEditing) {
      setFormFields({ objective: addTodayDate(form?.objective) });
    }
  };

  const handleCancelEdit = () => {
    const action = () => {
      if (wasOpenedInEditMode) {
        setShowModal(false);
      } else {
        setIsEditing(false);
      }
      resetForm();
    };

    if (hasChanged) {
      setCloseAction(() => action);
      setShowConfirmClose(true);
    } else {
      action();
    }
  };

  const handleHideModal = () => {
    const action = () => {
      setShowModal(false);
      resetForm();
    };

    if (hasChanged && isEditing) {
      setCloseAction(() => action);
      setShowConfirmClose(true);
    } else {
      action();
    }
  };

  const handleClickEdit = () => {
    setIsEditing(true);
    setFormFields({ objective: addTodayDate(form?.objective) });
  };

  const renderModalFooter = () => {
    if (!isEditing) {
      return (
        <div className='dialog-footer'>
          <Button key='close-button' label='Cerrar' icon={PrimeIcons.TIMES} onClick={handleHideModal} className='p-button-text' />
          {hasEditFeature && (
            <Button
              key='edit-button'
              label='Editar'
              icon={PrimeIcons.PENCIL}
              onClick={handleClickEdit}
              className='p-button-primary'
            />
          )}
        </div>
      );
    }

    return (
      <div className='dialog-footer'>
        <Button
          key='cancel-button'
          label='Cancelar'
          severity='secondary'
          onClick={handleCancelEdit}
          className='p-button-text'
          disabled={isSaving}
        />
        <Button
          key='save-button'
          label='Guardar'
          icon={PrimeIcons.CHECK}
          onClick={handleSave}
          loading={isSaving}
          disabled={!hasChanged || isSaving}
          className='p-button-primary'
        />
      </div>
    );
  };

  return (
    <div className='objective-field'>
      <div className='objective-header'>
        <strong>Objetivo</strong>
        {hasEditFeature && (
          <Button
            icon={PrimeIcons.PENCIL}
            onClick={() => handleOpenModal(true)}
            text
            rounded
            className='p-button-text edit-button-small'
          />
        )}
      </div>
      <p>
        {truncatedText}
        {needsTruncation && <Button label='Ver más' onClick={() => handleOpenModal(false)} link className='see-more-button' />}
      </p>

      <Dialog
        header='Objetivo'
        visible={showModal}
        onHide={handleHideModal}
        className='objective-dialog big-modal'
        footer={renderModalFooter()}
        draggable={false}
        resizable={false}
        dismissableMask
      >
        {isEditing ? (
          <InputTextarea
            value={form?.objective ?? ''}
            onChange={(e) => setFormFields({ objective: e.target.value })}
            rows={5}
            autoResize
          />
        ) : (
          <p className='objective-text'>{objective}</p>
        )}
      </Dialog>

      <ConfirmDialog
        visible={showConfirmClose}
        onHide={() => setShowConfirmClose(false)}
        message='¿Quieres cerrar la ventana? Perderás los cambios sin guardar.'
        header='Confirmar cierre'
        icon='pi pi-exclamation-triangle'
        accept={() => {
          closeAction();
          setShowConfirmClose(false);
        }}
        reject={() => setShowConfirmClose(false)}
        acceptLabel='Sí'
        rejectLabel='No'
      />
    </div>
  );
};

